/**
 * Profile information and password (set/change) forms.
 */

import React from 'react';
import type { PasswordFormData, ProfileFormData } from './types';
import { getInitials } from './utils';

interface ProfileTabProps {
  profileData: ProfileFormData;
  setProfileData: React.Dispatch<React.SetStateAction<ProfileFormData>>;
  profileLoading: boolean;
  profileSuccess: string | null;
  onProfileSubmit: (e: React.FormEvent) => void;

  passwordData: PasswordFormData;
  setPasswordData: React.Dispatch<React.SetStateAction<PasswordFormData>>;
  passwordLoading: boolean;
  passwordSuccess: string | null;
  passwordError: string | null;
  hasPassword: boolean;
  onPasswordSubmit: (e: React.FormEvent) => void;
}

export const ProfileTab: React.FC<ProfileTabProps> = ({
  profileData,
  setProfileData,
  profileLoading,
  profileSuccess,
  onProfileSubmit,
  passwordData,
  setPasswordData,
  passwordLoading,
  passwordSuccess,
  passwordError,
  hasPassword,
  onPasswordSubmit,
}) => {
  return (
    <div className="space-y-4">
      {/* Profile Information */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-16 h-16 rounded-full bg-linear-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-xl">
            {getInitials(profileData.firstName || profileData.email)}
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {profileData.firstName || 'User'}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">{profileData.email}</p>
          </div>
        </div>

        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3">
          Profile Information
        </h3>
        <form onSubmit={onProfileSubmit} className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              First Name
            </label>
            <input
              type="text"
              value={profileData.firstName}
              onChange={(e) => setProfileData({ ...profileData, firstName: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Email
            </label>
            <input
              type="email"
              value={profileData.email}
              onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Company (Optional)
            </label>
            <input
              type="text"
              value={profileData.company}
              onChange={(e) => setProfileData({ ...profileData, company: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {profileSuccess && (
            <div className="text-sm text-green-600 dark:text-green-400">{profileSuccess}</div>
          )}

          <button
            type="submit"
            disabled={profileLoading}
            className="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {profileLoading ? 'Saving...' : 'Save Changes'}
          </button>
        </form>
      </div>

      {/* Password Change / Set Password */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-3">
          {hasPassword ? 'Change Password' : 'Set Password'}
        </h2>
        {!hasPassword && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            No password set. Add a password to enable email/password sign-in as an alternative to
            OAuth.
          </p>
        )}
        <form onSubmit={onPasswordSubmit} className="space-y-3">
          {hasPassword && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Current Password
              </label>
              <input
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) =>
                  setPasswordData({ ...passwordData, currentPassword: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {hasPassword ? 'New Password' : 'Password'}
            </label>
            <input
              type="password"
              value={passwordData.newPassword}
              onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
              minLength={8}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {hasPassword ? 'Confirm New Password' : 'Confirm Password'}
            </label>
            <input
              type="password"
              value={passwordData.confirmPassword}
              onChange={(e) =>
                setPasswordData({ ...passwordData, confirmPassword: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
              minLength={8}
            />
          </div>

          {passwordSuccess && (
            <div className="text-sm text-green-600 dark:text-green-400">{passwordSuccess}</div>
          )}

          {passwordError && (
            <div className="text-sm text-red-600 dark:text-red-400">{passwordError}</div>
          )}

          <button
            type="submit"
            disabled={passwordLoading}
            className="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {passwordLoading
              ? hasPassword
                ? 'Changing...'
                : 'Setting...'
              : hasPassword
                ? 'Change Password'
                : 'Set Password'}
          </button>
        </form>
      </div>
    </div>
  );
};
